/**
 * Saudi phone handling. No dependency, no locale database — the lead form only
 * ever needs one country, and a 200 KB phone library for that is an insult to
 * a 4G connection.
 */

const ARABIC_INDIC_ZERO = 0x0660;
const EASTERN_ARABIC_INDIC_ZERO = 0x06f0;

/**
 * Fold Arabic-Indic (٠١٢٣) and Eastern Arabic-Indic (۰۱۲۳) digits to Western.
 *
 * Buyers type on Arabic keyboards; the interface renders Western numerals
 * everywhere (§3) but must never reject a number for being typed in the other
 * script.
 */
export function toWesternDigits(input: string): string {
  let out = '';
  for (const char of input) {
    const code = char.codePointAt(0) ?? 0;
    if (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) {
      out += String(code - ARABIC_INDIC_ZERO);
    } else if (
      code >= EASTERN_ARABIC_INDIC_ZERO &&
      code <= EASTERN_ARABIC_INDIC_ZERO + 9
    ) {
      out += String(code - EASTERN_ARABIC_INDIC_ZERO);
    } else {
      out += char;
    }
  }
  return out;
}

/**
 * Normalise any of the formats a Saudi buyer actually types into E.164.
 *
 *   05X XXX XXXX · 5XXXXXXXX · +9665XXXXXXXX · 009665XXXXXXXX · 9665XXXXXXXX
 *   011 XXX XXXX (landline, national significant number starting 1–4)
 *
 * Returns `null` when the input cannot be a Saudi number. Callers show the
 * field error; they never guess.
 */
export function normalizeSaudiPhone(raw: string): string | null {
  const digitsOnly = toWesternDigits(raw).replace(/[\s\-().‏‎]/g, '');
  let rest = digitsOnly;

  if (rest.startsWith('+966')) rest = rest.slice(4);
  else if (rest.startsWith('00966')) rest = rest.slice(5);
  else if (rest.startsWith('966')) rest = rest.slice(3);
  else if (rest.startsWith('0')) rest = rest.slice(1);

  if (!/^[0-9]+$/.test(rest)) return null;

  // Mobile: 9 digits beginning with 5.
  if (/^5[0-9]{8}$/.test(rest)) return `+966${rest}`;
  // Landline: 9 digits beginning with an area code 1–4.
  if (/^[1-4][0-9]{8}$/.test(rest)) return `+966${rest}`;

  return null;
}

export function isSaudiMobile(e164: string): boolean {
  return /^\+9665[0-9]{8}$/.test(e164);
}

/** `+966512345678` → `05 1234 5678`. Display only; storage stays E.164. */
export function formatSaudiPhoneForDisplay(e164: string): string {
  const match = /^\+966([0-9]{9})$/.exec(e164);
  if (!match) return e164;
  const national = match[1] as string;
  return `0${national.slice(0, 2)} ${national.slice(2, 5)} ${national.slice(5)}`;
}

/**
 * `wa.me` target for a unified 9200 number or a mobile.
 * Unified numbers are not WhatsApp-capable, so callers must fall back to `tel:`
 * when this returns null rather than producing a dead link.
 */
export function whatsappLink(
  e164OrDigits: string,
  messageAr: string,
): string | null {
  const digits = toWesternDigits(e164OrDigits).replace(/[^0-9]/g, '');
  if (!/^9665[0-9]{8}$/.test(digits)) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(messageAr)}`;
}

/** `tel:` target that works for both unified 920 numbers and mobiles. */
export function telLink(raw: string): string {
  const digits = toWesternDigits(raw).replace(/[^0-9+]/g, '');
  return `tel:${digits}`;
}
